import { ApiError } from '@/core/api/http'
import { apiDelete, apiFirst, apiGetOneOrNull, apiListAll, apiPatch, apiPost } from '@/core/api/resources'

export type ModulRow = { id: string; name: string }

export type MassnahmeModulRow = {
  modulId: string
  name: string
  ueAnzahl: number | null
}

export async function listModulnCatalog(organisationId: string): Promise<ModulRow[]> {
  const rows = await apiListAll('/modul', { organisation_id: organisationId, order: 'name' })
  return rows.map((r) => ({ id: r.id, name: r.name }))
}

export async function listMassnahmeModuln(massnahmeId: string): Promise<MassnahmeModulRow[]> {
  const rows = await apiListAll('/massnahme_modul', {
    massnahme_id: massnahmeId,
    include: ['modul'],
    order: 'reihenfolge',
  })
  return rows.map((r) => ({
    modulId: r.modul_id,
    name: r.modul?.name ?? '',
    ueAnzahl: r.ue_anzahl ?? null,
  }))
}

export async function listCoachingModuln(massnahmeId: string | null): Promise<ModulRow[]> {
  if (!massnahmeId) return []
  const rows = await listMassnahmeModuln(massnahmeId)
  return rows.map((r) => ({ id: r.modulId, name: r.name }))
}

export async function linkModul(
  massnahmeId: string,
  organisationId: string,
  name: string,
  ueAnzahl: number | null,
): Promise<void> {
  const existing = await apiFirst('/modul', { organisation_id: organisationId, name, limit: 1 })
  const modulId =
    existing?.id ?? (await apiPost('/modul', { organisation_id: organisationId, name })).id
  const last = await apiFirst('/massnahme_modul', {
    massnahme_id: massnahmeId,
    order: 'reihenfolge',
    desc: true,
    limit: 1,
  })
  const nextReihenfolge = (last?.reihenfolge ?? 0) + 1
  await apiPost('/massnahme_modul', {
    massnahme_id: massnahmeId,
    modul_id: modulId,
    ue_anzahl: ueAnzahl,
    reihenfolge: nextReihenfolge,
  })
}

export async function unlinkModul(massnahmeId: string, modulId: string): Promise<void> {
  await apiDelete('/massnahme_modul/{massnahme_id}/{modul_id}', {
    massnahme_id: massnahmeId,
    modul_id: modulId,
  })
}

export async function renameModul(modulId: string, name: string): Promise<void> {
  await apiPatch('/modul/{id}', { id: modulId }, { name })
}

export async function updateMassnahmeModulUe(
  massnahmeId: string,
  modulId: string,
  ueAnzahl: number | null,
): Promise<void> {
  await apiPatch(
    '/massnahme_modul/{massnahme_id}/{modul_id}',
    { massnahme_id: massnahmeId, modul_id: modulId },
    { ue_anzahl: ueAnzahl },
  )
}

export function otherModulesUeSum(moduln: readonly MassnahmeModulRow[], excludeModulId: string): number {
  return moduln.filter((m) => m.modulId !== excludeModulId).reduce((sum, m) => sum + (m.ueAnzahl ?? 0), 0)
}

export function maxAcceptedModulUe(budget: number, otherSum: number): number {
  return Math.max(0, budget - otherSum)
}

export async function listMassnahmeUeSums(): Promise<Map<string, number>> {
  const rows = await apiListAll('/massnahme_modul')
  const sums = new Map<string, number>()
  for (const row of rows) {
    sums.set(row.massnahme_id, (sums.get(row.massnahme_id) ?? 0) + (row.ue_anzahl ?? 0))
  }
  return sums
}

export async function getModul(modulId: string): Promise<ModulRow | null> {
  const row = await apiGetOneOrNull('/modul/{id}', { id: modulId })
  return row ? { id: row.id, name: row.name } : null
}

export async function createModul(organisationId: string, name: string): Promise<void> {
  await apiPost('/modul', { organisation_id: organisationId, name })
}

export async function listModulUsageCounts(): Promise<Map<string, number>> {
  const rows = await apiListAll('/massnahme_modul')
  const counts = new Map<string, number>()
  for (const row of rows) {
    counts.set(row.modul_id, (counts.get(row.modul_id) ?? 0) + 1)
  }
  return counts
}

export function isForeignKeyViolation(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && /foreign key|still (linked|referenced)/i.test(error.detailText)
}

export async function deleteModul(modulId: string): Promise<void> {
  await apiDelete('/modul/{id}', { id: modulId })
}
